import { ShareAltOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';
import oddsApi from 'api/oddsApi';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import InfomationIcon from 'assets/icons/InfomationIcon';
import BigNumber from 'bignumber.js';
import { showConfirmModal, showSuccessModal } from 'components/Modals/CommonModals';
import {
  MARKET_MONEYLINES,
  MARKET_SPREADS,
  MARKET_TOTALS,
  MORE_WAGERS_FIRST_HALF,
  MORE_WAGERS_SECOND_HALF,
  WAGERS_PLAYER_PROPS,
} from 'consts/app';
import Typo from 'consts/typography';
import { selectCurrentConverter } from 'features/home/homeSlice';
import ShareSingleBetTwitter from 'features/twitter/ShareSingleBetTwiiter';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { BetResponseModel } from 'models';
import React, { useMemo, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { selectTbetPrice } from 'redux/userSlice';
import { getMarketBetValue, getScore, renderScore, statusColor } from 'utils/betUtils';
import { isPhone, isTablet } from 'utils/commonUtils';

interface MyBetSingleProps {
  bet: BetResponseModel;
  tab: number;
}

export const getDateWithFormat24h = (date: string): string => {
  return new Date(date).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24-hour format
  });
};

const getMarketName = (
  market: string,
):
  | 'Spread'
  | 'Moneyline'
  | 'Total point'
  | 'First half spread'
  | 'First half money'
  | 'First half total'
  | 'Second half spread'
  | 'Second half money'
  | 'Second half total' => {
  if (MORE_WAGERS_FIRST_HALF[0].markets.includes(market)) return 'First half spread';
  if (MORE_WAGERS_FIRST_HALF[1].markets.includes(market)) return 'First half money';
  if (MORE_WAGERS_FIRST_HALF[2].markets.includes(market)) return 'First half total';
  if (MORE_WAGERS_SECOND_HALF[0].markets.includes(market)) return 'Second half spread';
  if (MORE_WAGERS_SECOND_HALF[1].markets.includes(market)) return 'Second half money';
  if (MORE_WAGERS_SECOND_HALF[2].markets.includes(market)) return 'Second half total';
  if (MARKET_SPREADS.includes(market)) return 'Spread';
  if (MARKET_MONEYLINES.includes(market)) return 'Moneyline';
  return 'Total point';
};

const getTypeTeam = (bet: BetResponseModel, teamName: string): 'Home' | 'Tie' | 'Away' => {
  if (teamName === bet.betDetails[0].game.homeTeam) return 'Home';
  else if (teamName === bet.betDetails[0].game.awayTeam) return 'Away';
  return 'Tie';
};

const getBigDecimalPossibleWin = (amount: number, decimalPrice: number) => {
  const amountBigNumber = new BigNumber(amount);
  return amountBigNumber.times(new BigNumber(decimalPrice)).minus(amountBigNumber);
};

const MyBetSingle: React.FunctionComponent<MyBetSingleProps> = ({ tab, bet }) => {
  const t = useAppTranslation();
  const platform = useAppSelector(platformStates);
  const converter = useAppSelector(selectCurrentConverter);
  const { isDesktop, isLargeDesktop } = platform;
  const handleResponseError = useHandleResponseError();
  const [isShowShare, setIsShowShare] = useState<boolean>(false);
  const colStyle1 = `col-span-3 primary-text-color m-0 ${
    isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
  } font-medium`;
  const colStyle2 = `primary-text-color col-span-2 m-0 ${
    isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
  } font-normal`;

  const handleShareBet = async () => {
    const onOk = async () => {
      const { ok, error } = await oddsApi.shareBets({
        betId: bet.id,
      });
      if (ok) {
        showSuccessModal({
          title: t('sharebet.success.title'),
          content: t('sharebet.success.message'),
        });
      }
      if (error) {
        handleResponseError(error);
      }
    };

    showConfirmModal({
      content: <p>{t('sharebet.popup.sharebet-content')}</p>,
      width: 360,
      icon: <InfomationIcon />,
      onOk: onOk,
      okText: t('sharebet.popup.button-ok'),
      cancelText: t('sharebet.popup.button-cancel'),
    });
  };

  const isPlayerPropsBet = useMemo(() => {
    return WAGERS_PLAYER_PROPS.includes(bet.betDetails[0]?.market?.market);
  }, [bet.betDetails]);

  const betAmount = useMemo(() => {
    return `${bet.amount} $`;
  }, [bet.id]);

  const winBetAmount = useMemo(() => {
    const winAmount =
      tab === 1
        ? getBigDecimalPossibleWin(
            bet.amount,
            bet?.betDetails[0].type === 'BET_GAME'
              ? bet.betDetails[0].market.decimalPrice
              : bet.betDetails[0].futureMarket?.decimalPrice,
          )
        : new BigNumber(bet.winAmount);

    return `${winAmount} $`;
  }, [bet.id]);

  const statusClassname = (betStatus: string) => {
    const className = statusColor[betStatus] || 'bet-cancel';
    return `col-span-2 m-0 ${
      isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
    } font-bold ${className}`;
  };

  return (
    <div className="my-bet">
      <div className="my-bet__header primary-text-color">
        <Row className="my-bet__title">
          <Col flex={4}>
            <p
              className={`primary-text-color ${
                isDesktop || isLargeDesktop ? Typo.p3 : Typo.p5
              } m-0 `}
            >
              {t('myBet.singleBet')}
            </p>
          </Col>
          <Col flex={1} className="mr-2">
            <div className="my-bet__social_btn">
              <span
                className={`primary-text-color ${
                  isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7
                } m-0`}
              >
                {`${t('myBetSingle.share')}: `}
              </span>
              <button type="button" onClick={() => setIsShowShare(true)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  width="15"
                  height="15"
                  viewBox="0 0 50 50"
                >
                  <path d="M 5.9199219 6 L 20.582031 27.375 L 6.2304688 44 L 9.4101562 44 L 21.986328 29.421875 L 31.986328 44 L 44 44 L 28.681641 21.669922 L 42.199219 6 L 39.029297 6 L 27.275391 19.617188 L 17.933594 6 L 5.9199219 6 z M 9.7167969 8 L 16.880859 8 L 40.203125 42 L 33.039062 42 L 9.7167969 8 z"></path>
                </svg>
              </button>
            </div>
          </Col>
          <Col>
            <Button
              shape="round"
              icon={<ShareAltOutlined />}
              size="small"
              className="share-bet__btn"
              onClick={handleShareBet}
            />
          </Col>
        </Row>
        {bet?.betDetails[0].type === 'BET_GAME' ? (
          <span className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p5}`}>
            {bet?.betDetails[0].game.homeTeam} vs {bet?.betDetails[0].game.awayTeam}
          </span>
        ) : (
          <span className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p5}`}>
            {bet?.betDetails[0].futureOdds?.name}
          </span>
        )}
      </div>
      {bet?.betDetails[0].type === 'BET_GAME' ? (
        <div className="my-bet__content">
          {isPlayerPropsBet && (
            <div className="col-span-5">
              <span
                className={`${
                  isPhone(platform) ? Typo.p8 : isTablet(platform) ? Typo.p6 : Typo.p4
                } primary-text-color font-bold`}
              >
                {bet.betDetails[0].market.marketName}
              </span>
            </div>
          )}
          {tab === 2 ? (
            <>
              <p className={colStyle1}>{t('myBet.result')}</p>
              <p className={statusClassname(bet.status)}>{bet.status}</p>
            </>
          ) : null}
          {!MARKET_TOTALS.includes(bet.betDetails[0].market.market) &&
          !MORE_WAGERS_FIRST_HALF[2].markets.includes(bet.betDetails[0].market.market) &&
          !MORE_WAGERS_SECOND_HALF[2].markets.includes(bet.betDetails[0].market.market) &&
          bet?.betDetails[0].game.sport.group !== 'Soccer' ? (
            <>
              <p className={colStyle1}>{bet.betDetails[0].market.selection}</p>
              <p className={colStyle2}>{isPlayerPropsBet ? '' : t('myBet.win')}</p>
            </>
          ) : null}
          {bet?.betDetails[0].game.sport.group === 'Soccer' && (
            <>
              <p className={colStyle1}>{getTypeTeam(bet, bet.betDetails[0].market.selection)}</p>
              <p className={colStyle2}>{t('myBet.win')}</p>
            </>
          )}
          <p className={colStyle1}>{getMarketName(bet.betDetails[0].market.market)}</p>
          <p className={colStyle2}>
            {getMarketBetValue(
              bet.betDetails[0].market.market,
              converter,
              bet.betDetails[0].market.price,
              bet.betDetails[0].market.decimalPrice,
              bet.betDetails[0].market.betPoints || 0,
              bet.betDetails[0].market.selectionLine || '',
            )}
          </p>
          <p className={colStyle1}>{t('myBet.betAmount')}</p>
          <p className={colStyle2}>{betAmount}</p>
          {tab === 1 && (
            <>
              <p className={colStyle1}>{t('myBet.possibleWin')}</p>
              <p className={colStyle2}>{winBetAmount}</p>
            </>
          )}
          {bet.status === 'WIN' && tab === 2 && (
            <>
              <p className={colStyle1}>{t('myBet.win')}</p>
              <p className={colStyle2}>{winBetAmount}</p>
            </>
          )}
          {tab === 2 && (
            <>
              <p className={colStyle1}>{t('myBet.score')}</p>
              <p className={colStyle2}>{getScore(bet.betDetails[0])}</p>
            </>
          )}
        </div>
      ) : (
        <div className="my-bet__content">
          {tab === 2 ? (
            <>
              <p className={colStyle1}>{t('myBet.result')}</p>
              <p className={statusClassname(bet.status)}>{bet.status}</p>
            </>
          ) : null}
          <p className={colStyle1}>
            {bet.betDetails[0].future?.league} - {bet.betDetails[0].future?.name}
          </p>
          <p className={colStyle1}>{t('myBet.betAmount')}</p>
          <p className={colStyle2}>{betAmount}</p>
          {tab === 1 && (
            <>
              <p className={colStyle1}>{t('myBet.possibleWin')}</p>
              <p className={colStyle2}>{winBetAmount}</p>
            </>
          )}
          {bet.status === 'WIN' && tab === 2 && (
            <>
              <p className={colStyle1}>{t('myBet.win')}</p>
              <p className={colStyle2}>{winBetAmount}</p>
            </>
          )}
        </div>
      )}
      <div className="my-bet__info" style={{}}>
        <p
          className={`primary-text-color ${
            isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
          } font-bold`}
        >
          BET ID: {bet.id}
        </p>
        <p
          className={`primary-text-color ${
            isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
          } font-bold`}
        >
          PLACED: {getDateWithFormat24h(bet.createdDate)}
        </p>
      </div>
      <ShareSingleBetTwitter
        visible={isShowShare}
        closeModal={() => setIsShowShare(false)}
        bet={bet}
        tab={tab}
        className="common-modal withdraw-modal"
      />
    </div>
  );
};

export default MyBetSingle;
